import React from "react";
import { Box, Checkbox, Typography } from "@mui/material";
import { Serializer, QuestionCheckboxModel, SurveyModel } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase
} from "survey-react-ui";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import { ArrowForward } from "@mui/icons-material";

class QuestionCheckboxWithButtonModel extends QuestionCheckboxModel {
  getType() {
    return "checkbox-with-button";
  }
}

Serializer.addClass(
  "checkbox-with-button",
  [],
  () => new QuestionCheckboxWithButtonModel(""),
  "checkbox"
);

interface CheckboxWithButtonState {
  canComplete: boolean;
}

class SurveyQuestionCheckboxWithButton extends SurveyQuestionElementBase {
  state: CheckboxWithButtonState = { canComplete: false };
  private valueChangedHandler: () => void;
  private pageChangedHandler: () => void;

  constructor(props: any) {
    super(props);
    this.valueChangedHandler = () => this.updateCanComplete();
    this.pageChangedHandler = () => this.updateCanComplete();
  }

  private get questionModel(): QuestionCheckboxWithButtonModel {
    return (
      (this.questionBase as QuestionCheckboxWithButtonModel) ||
      ((this.props as any).element as QuestionCheckboxWithButtonModel)
    );
  }

  private get survey(): SurveyModel {
    return this.questionModel.survey as SurveyModel;
  }

  componentDidMount() {
    super.componentDidMount();
    const survey = this.survey;
    if (survey) {
      survey.onValueChanged.add(this.valueChangedHandler);
      survey.onCurrentPageChanged.add(this.pageChangedHandler);
    }
    this.updateCanComplete();
  }

  componentWillUnmount() {
    const survey = this.survey;
    if (survey) {
      survey.onValueChanged.remove(this.valueChangedHandler);
      survey.onCurrentPageChanged.remove(this.pageChangedHandler);
    }
    super.componentWillUnmount();
  }

  private updateCanComplete() {
    const survey = this.survey;
    if (!survey) {
      return;
    }
    const canComplete = survey.isLastPage && !survey.hasErrors();
    if (this.state.canComplete !== canComplete) {
      this.setState({ canComplete });
    }
  }

  private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const question = this.questionModel;
    const choice = question.choices[0];
    if (event.target.checked) {
      question.value = [choice.value];
    } else {
      question.value = [];
    }
  };

  private handleSubmit = () => {
    const survey = this.survey;
    if (survey && survey.validate()) {
      survey.tryComplete();
    }
  };

  protected renderElement(): React.ReactElement {
    const question = this.questionModel;
    const choice = question.choices[0];
    const checked = Array.isArray(question.value)
      ? question.value.includes(choice.value)
      : false;
    const buttonDisabled = !checked || !this.state.canComplete;

    return (
      <Box className="terms-checkbox-with-button">
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flex: 1,
            minWidth: 0
          }}
        >
          <Checkbox checked={checked} onChange={this.handleChange} />
          <Typography sx={{ color: COLORS.BLACK, fontWeight: 600 }}>
            {choice.text || question.title}
          </Typography>
        </Box>
        <Box sx={{ flexShrink: 0 }}>
          <GLButton
            text="Continue"
            disabled={buttonDisabled}
            onClick={this.handleSubmit}
            rightIcon={<ArrowForward />}
          />
        </Box>
      </Box>
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(
  "checkbox-with-button",
  (props) => {
    return React.createElement(SurveyQuestionCheckboxWithButton, props);
  }
);

export default SurveyQuestionCheckboxWithButton;
